/**
 * Google Picker Page
 *
 * This page is served on the central auth domain (e.g., mylogin.dev.kenbakke.com)
 * and handles the Google Drive picker flow for multi-tenant environments.
 *
 * Flow:
 * 1. Tenant subdomain opens this page in a popup with returnOrigin parameter
 * 2. This page handles Google OAuth authentication
 * 3. Opens the Google Picker modal for file/folder selection
 * 4. Sends the result back to the tenant via postMessage
 * 5. Closes the popup
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import useGoogleDrivePicker from "google-drive-picker";
import { Button, CircularProgress, Typography, Box, Alert } from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";

import { googleDriveConfig } from "../../../common/utilities/constants/constants";
import {
    sendPickerResultAndClose,
    notifyPickerReady,
    notifyPickerCancel,
    isValidOrigin,
    getMainDomain
} from "../../../common/utilities/google-picker/google-picker-message.util";

import "./google-picker.scss";

type PickerStatus = "initializing" | "ready" | "picking" | "success" | "error" | "invalid-origin";

const GooglePickerPage = () => {
    console.log("GooglePickerPage rendering, pathname:", window.location.pathname);

    const [status, setStatus] = useState<PickerStatus>("initializing");
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [openPicker, authRes] = useGoogleDrivePicker();

    // Use a ref to always have access to the latest token in callbacks
    const accessTokenRef = useRef<string>("");

    // Get the return origin from URL parameters
    const returnOrigin = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get("returnOrigin") || "";
    }, []);

    // Validate the return origin
    const isOriginValid = useMemo(() => {
        if (!returnOrigin) return false;

        const mainDomain = getMainDomain();
        return isValidOrigin(returnOrigin, [mainDomain]);
    }, [returnOrigin]);

    // Update access token ref when auth response changes
    useEffect(() => {
        if (authRes?.access_token) {
            console.log("GooglePickerPage: Got access token from auth response");
            accessTokenRef.current = authRes.access_token;
        }
    }, [authRes]);

    // Initialize and validate on mount
    useEffect(() => {
        if (!returnOrigin) {
            setStatus("invalid-origin");
            setErrorMessage("Missing return origin parameter");
            return;
        }

        if (!isOriginValid) {
            setStatus("invalid-origin");
            setErrorMessage(
                "Invalid origin. This picker can only be opened from authorized domains."
            );
            return;
        }

        if (!googleDriveConfig.clientId || !googleDriveConfig.developerKey) {
            setStatus("error");
            setErrorMessage("Google Drive configuration is missing");
            return;
        }

        // Notify parent that picker is ready
        setStatus("ready");
        notifyPickerReady(returnOrigin);
    }, [returnOrigin, isOriginValid]);

    // Handle picker open
    const handleOpenPicker = useCallback(() => {
        if (!googleDriveConfig.clientId || !googleDriveConfig.developerKey) {
            setErrorMessage("Google Drive configuration is missing");
            setStatus("error");
            return;
        }

        setStatus("picking");

        openPicker({
            clientId: googleDriveConfig.clientId,
            developerKey: googleDriveConfig.developerKey,
            viewId: "DOCS",
            showUploadView: true,
            showUploadFolders: true,
            supportDrives: true,
            multiselect: false,
            setIncludeFolders: true,
            setSelectFolderEnabled: true,
            callbackFunction: data => {
                console.log("GooglePickerPage: Picker callback fired, action:", data.action);

                if (data.action === "cancel") {
                    console.log("GooglePickerPage: User cancelled picker");
                    notifyPickerCancel(returnOrigin);
                } else if (data.action === "picked" && data.docs && data.docs.length > 0) {
                    const currentToken = accessTokenRef.current;
                    console.log("GooglePickerPage: User picked file, URL:", data.docs[0].url);
                    console.log("GooglePickerPage: Access token available:", !!currentToken);
                    console.log("GooglePickerPage: Sending result to returnOrigin:", returnOrigin);
                    console.log("GooglePickerPage: window.opener exists:", !!window.opener);

                    setStatus("success");
                    sendPickerResultAndClose(
                        {
                            success: true,
                            url: data.docs[0].url,
                            accessToken: currentToken
                        },
                        returnOrigin
                    );
                }
            }
        });
    }, [openPicker, returnOrigin]);

    // Handle cancel
    const handleCancel = useCallback(() => {
        notifyPickerCancel(returnOrigin);
    }, [returnOrigin]);

    // Render based on status
    const renderContent = () => {
        switch (status) {
            case "initializing":
                return (
                    <Box className="google-picker-page__loading">
                        <CircularProgress size={40} />
                        <Typography variant="body1">Initializing...</Typography>
                    </Box>
                );

            case "invalid-origin":
                return (
                    <Box className="google-picker-page__error">
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {errorMessage}
                        </Alert>
                        <Typography variant="body2" color="text.secondary">
                            Please close this window and try again from the application.
                        </Typography>
                    </Box>
                );

            case "error":
                return (
                    <Box className="google-picker-page__error">
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {errorMessage}
                        </Alert>
                        <Button variant="outlined" onClick={handleCancel}>
                            Close
                        </Button>
                    </Box>
                );

            case "ready":
                return (
                    <Box className="google-picker-page__ready">
                        <FolderIcon sx={{ fontSize: 64, color: "#4285f4", mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            Select Google Drive Folder
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Click the button below to open Google Drive and select a folder or file
                            to import.
                        </Typography>
                        <Box className="google-picker-page__actions">
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={handleOpenPicker}
                                startIcon={
                                    <img
                                        src="https://www.gstatic.com/images/branding/product/1x/drive_2020q4_48dp.png"
                                        alt="Google Drive"
                                        style={{ width: 24, height: 24 }}
                                    />
                                }
                            >
                                Open Google Drive
                            </Button>
                            <Button variant="text" onClick={handleCancel} sx={{ mt: 1 }}>
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                );

            case "picking":
                return (
                    <Box className="google-picker-page__loading">
                        <CircularProgress size={40} />
                        <Typography variant="body1">
                            Please select a folder in the Google Drive picker...
                        </Typography>
                        <Button variant="text" onClick={handleCancel} sx={{ mt: 2 }}>
                            Cancel
                        </Button>
                    </Box>
                );

            case "success":
                return (
                    <Box className="google-picker-page__loading">
                        <CircularProgress size={40} />
                        <Typography variant="body1">Selection complete. Closing...</Typography>
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <div className="google-picker-page">
            <div className="google-picker-page__container">{renderContent()}</div>
        </div>
    );
};

export default GooglePickerPage;
